import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";

import { Coding } from "@medplum/fhirtypes";

import { Accordion, Button, Flex, Group, Stack, Textarea, UnstyledButton } from "@mantine/core";

import {
  IconAlertTriangle,
  IconCircleCheck,
  IconEdit,
  IconMedicalCross,
  IconTrash,
} from "@tabler/icons-react";

import { Loading } from "@/components/Loading";
import RemovalConfirmationModal from "@/components/RemovalConfirmationModal";
import { TextWrapper } from "@/components/TextWrapper";
import { EncounterDiagnosesFormType } from "@/encounters/zod-schemas/encounter-answers";
import PermissionGate from "@/permission/components/PermissionGate";
import { PROVIDER_BILLER_SCRIBE_PERMISSION_FLAGS } from "@/permission/constants/practitioner-permissions";
import { useAppDispatch } from "@/store";
import { selectIsDiagnosisInProblemList } from "@/store/slices/client-problems";
import { selectAllActiveProcedures, setTargetProcedure } from "@/store/slices/procedures";
import { ProceduresFormTypeWithId } from "@/zod-schemas/procedures";

interface DxPlanListItemProps {
  diagnosis: EncounterDiagnosesFormType;
  canEditDiagnoses: boolean;
  handleDeleteDiagnosis: (diagnosis: EncounterDiagnosesFormType) => void;
  handleEditDiagnosis: (diagnosis: EncounterDiagnosesFormType) => void;
}

interface DxPlanListProps {
  diagnoses: EncounterDiagnosesFormType[];
  canEditDiagnoses: boolean;
  handleDeleteDiagnosis: (diagnosis: EncounterDiagnosesFormType) => void;
  handleEditDiagnosis: (diagnosis: EncounterDiagnosesFormType) => void;
  onProcedureDiagnosisEditClick?: () => void;
}

const getDiagnosisKey = (diagnosis: EncounterDiagnosesFormType) => {
  return `${diagnosis.encounterDiagnosisCode.code}-${diagnosis.encounterDiagnosisAddToProblemList}-${diagnosis.encounterDiagnosisNotes}`;
};

const getDiagnosisFromProcedures = (
  procedures: ProceduresFormTypeWithId[],
): {
  diagnosis: Coding;
  procedure: ProceduresFormTypeWithId;
}[] => {
  return procedures.reduce(
    (acc, procedure) => {
      Object.values(procedure.cptDiagnosesFormset ?? {}).forEach((diagnosis) => {
        acc.push({
          diagnosis: diagnosis.cptDiagnosesFormsetDiagnosis as Coding,
          procedure,
        });
      });
      return acc;
    },
    [] as { diagnosis: Coding; procedure: ProceduresFormTypeWithId }[],
  );
};

const getProcedureDiagnosisKey = (procedureDiagnosis: Coding) => {
  return `${procedureDiagnosis?.code}-${procedureDiagnosis?.display}`;
};

const getProcedureDiagnosisKeys = (procedures: ProceduresFormTypeWithId[]) =>
  procedures
    .map((procedure) =>
      Object.values(procedure.cptDiagnosesFormset ?? {}).map((diagnosis) =>
        getProcedureDiagnosisKey(diagnosis.cptDiagnosesFormsetDiagnosis as Coding),
      ),
    )
    .flat();

function DxPlanListItem({
  diagnosis,
  canEditDiagnoses,
  handleDeleteDiagnosis,
  handleEditDiagnosis,
}: DxPlanListItemProps) {
  const [noteValue, _setNoteValue] = useState<string>(diagnosis.encounterDiagnosisNotes ?? "");
  const [isRemovalConfirmationModalOpen, setIsRemovalConfirmationModalOpen] = useState(false);

  const isInProblemList = useSelector(selectIsDiagnosisInProblemList)(
    diagnosis.encounterDiagnosisCode.display,
  );

  return (
    <>
      <RemovalConfirmationModal
        actionText="Yes, Delete"
        isOpen={isRemovalConfirmationModalOpen}
        title="Are you sure you want to delete?"
        itemId={diagnosis.encounterDiagnosisCode.code}
        handleAction={() => handleDeleteDiagnosis(diagnosis)}
        handleClose={() => setIsRemovalConfirmationModalOpen(false)}
        description="Performing this action will permanently delete this diagnosis"
      />
      <Accordion.Item
        p={24}
        bg="white"
        value={getDiagnosisKey(diagnosis)}
        style={{
          border: "1px solid var(--mantine-color-cream-4)",
        }}
      >
        <Stack gap="xs">
          <Flex justify="space-between">
            <Flex gap="xs" align="center">
              <IconMedicalCross size={24} color="var(--mantine-color-gray-6)" />
              <TextWrapper fz="sm">
                {diagnosis.encounterDiagnosisCode.code} - {diagnosis.encounterDiagnosisCode.display}
              </TextWrapper>
            </Flex>
            {canEditDiagnoses && (
              <PermissionGate requiredPermissions={PROVIDER_BILLER_SCRIBE_PERMISSION_FLAGS}>
                <Flex gap="xs" align="center">
                  <>
                    <Button
                      color="burgundy.9"
                      variant="transparent"
                      leftSection={<IconEdit size={24} />}
                      onClick={() => handleEditDiagnosis(diagnosis)}
                    >
                      Edit
                    </Button>
                    <Button
                      color="red.8"
                      variant="transparent"
                      leftSection={<IconTrash size={24} />}
                      onClick={() => setIsRemovalConfirmationModalOpen(true)}
                    >
                      Delete
                    </Button>
                  </>
                  <Flex justify="flex-end">
                    <Accordion.Control />
                  </Flex>
                </Flex>
              </PermissionGate>
            )}
          </Flex>
          {isInProblemList && (
            <Flex
              px={4}
              fz="sm"
              gap={4}
              bg="blue.0"
              align="center"
              w="fit-content"
              style={{ borderRadius: 4 }}
            >
              <IconCircleCheck size={16} fontWeight={600} color="var(--mantine-color-blue-7)" />
              <TextWrapper fz="sm" fw={600} c="blue.7">
                On client side panel
              </TextWrapper>
            </Flex>
          )}
          {!isInProblemList && diagnosis.encounterDiagnosisAddToProblemList && canEditDiagnoses && (
            <Flex
              px={4}
              fz="sm"
              gap={4}
              bg="orange.0"
              align="center"
              w="fit-content"
              style={{ borderRadius: 4 }}
            >
              <IconAlertTriangle size={16} fontWeight={600} color="var(--mantine-color-orange-7)" />
              <TextWrapper fz="sm" fw={700} c="orange.7">
                Save or lock to Dx to display on client side panel
              </TextWrapper>
            </Flex>
          )}
        </Stack>
        <Accordion.Panel>
          <Textarea readOnly label="Notes" value={noteValue} />
        </Accordion.Panel>
      </Accordion.Item>
    </>
  );
}

function DxPlanProcedureListItem({
  diagnosis,
  procedure,
  onEditClick,
  canEditDiagnoses,
}: {
  diagnosis: Coding;
  procedure: ProceduresFormTypeWithId;
  onEditClick?: () => void;
  canEditDiagnoses: boolean;
}) {
  const dispatch = useAppDispatch();

  const handleEditClick = () => {
    if (!onEditClick) return;
    dispatch(setTargetProcedure(procedure));
    onEditClick();
  };

  return (
    <Accordion.Item
      p={24}
      bg="white"
      value={getProcedureDiagnosisKey(diagnosis)}
      style={{
        border: "1px solid var(--mantine-color-cream-4)",
      }}
    >
      <Group wrap="nowrap" align="center" justify="space-between">
        <Flex p={0} gap="xs" align="center" style={{ minWidth: 0, flex: 1 }}>
          <IconMedicalCross
            size={24}
            style={{ flexShrink: 0 }}
            color="var(--mantine-color-gray-6)"
          />
          <TextWrapper style={{ wordBreak: "break-word" }}>
            {diagnosis.code} - {diagnosis.display}
          </TextWrapper>
        </Flex>
        <Flex justify="flex-end">
          <Accordion.Control />
        </Flex>
      </Group>
      <Accordion.Panel>
        <Stack mt="md" gap="md">
          <Flex p="md" bg="gray.0" justify="space-between" style={{ borderRadius: 4 }}>
            <TextWrapper fw={600} c="dark.2">
              CPT Code
            </TextWrapper>
            <TextWrapper
              fw={600}
              c="dark.9"
              style={{ wordBreak: "break-word", textAlign: "right" }}
            >
              {procedure.cptCode?.code} - {procedure.cptCode?.display}
            </TextWrapper>
          </Flex>
          {canEditDiagnoses && (
            <PermissionGate requiredPermissions={PROVIDER_BILLER_SCRIBE_PERMISSION_FLAGS}>
              <Flex p="md" bg="gray.0" justify="space-between" style={{ borderRadius: 4 }}>
                <TextWrapper fw={600} c="dark.2">
                  To edit this Dx please go back to Objective tab
                </TextWrapper>
                <UnstyledButton onClick={handleEditClick}>
                  <TextWrapper fw={600} c="dark.9" td="underline">
                    Edit in Objective Tab
                  </TextWrapper>
                </UnstyledButton>
              </Flex>
            </PermissionGate>
          )}
        </Stack>
      </Accordion.Panel>
    </Accordion.Item>
  );
}

export default function DxPlanList({
  diagnoses,
  canEditDiagnoses,
  handleDeleteDiagnosis,
  handleEditDiagnosis,
  onProcedureDiagnosisEditClick,
}: DxPlanListProps) {
  const procedures = useSelector(selectAllActiveProcedures);
  const [proceduresDiagnoses, setProceduresDiagnoses] = useState<
    { diagnosis: Coding; procedure: ProceduresFormTypeWithId }[]
  >([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const proceduresDiagnoses = getDiagnosisFromProcedures(procedures);
    setProceduresDiagnoses(proceduresDiagnoses);
    setIsLoading(false);
  }, [procedures]);

  const allKeys = useMemo(
    () => [
      ...diagnoses.map((diagnosis) => getDiagnosisKey(diagnosis)),
      ...getProcedureDiagnosisKeys(procedures),
    ],
    [diagnoses, procedures],
  );

  const [value, setValue] = useState<string[]>(allKeys);

  useEffect(() => {
    setValue(allKeys);
  }, [allKeys]);

  return (
    <>
      {!isLoading ? (
        <Accordion
          multiple
          value={value}
          variant="separated"
          onChange={setValue}
          defaultValue={allKeys}
          chevronPosition="right"
        >
          {proceduresDiagnoses.map((diagnosisData) => (
            <DxPlanProcedureListItem
              canEditDiagnoses={canEditDiagnoses}
              diagnosis={diagnosisData.diagnosis}
              procedure={diagnosisData.procedure}
              onEditClick={onProcedureDiagnosisEditClick}
              key={getProcedureDiagnosisKey(diagnosisData.diagnosis)}
            />
          ))}
          {diagnoses.map((diagnosis) => (
            <DxPlanListItem
              diagnosis={diagnosis}
              key={getDiagnosisKey(diagnosis)}
              canEditDiagnoses={canEditDiagnoses}
              handleEditDiagnosis={handleEditDiagnosis}
              handleDeleteDiagnosis={handleDeleteDiagnosis}
            />
          ))}
        </Accordion>
      ) : (
        <Loading size={24} height="sm" />
      )}
    </>
  );
}
